import Divider from '@rio-cloud/rio-uikit/components/divider/Divider';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import InfiniteScroll from 'react-infinite-scroller';
import { useIntl } from 'react-intl';

import SearchField from '@/components/atom/SearchField/SearchField';
import ThreadsListControlBar from '@/components/atom/ThreadsListControlBar/ThreadsListControlBar';
import ThreadsInTimespanList from '@/components/molecule/ThreadsInTimespanList/ThreadsInTimespanList';
import { useAppSelector } from '@/configuration/setup/hooks';
import { selectPagination } from '@/store/thread/threadSlice';
import { TChatThread } from '@/store/thread/types';

import { hasValidThreads } from '../utils';
import { SCROLLABLE_SECTION, THREADS_LIST_TEST_IDS } from './constants';
import { TThreadListProps } from './types';

const texts = {
  today: 'chatPage.chatThreadList.today.label',
  last7Days: 'chatPage.chatThreadList.last7Days.label',
  last30Days: 'chatPage.chatThreadList.last30Days.label',
  older: 'chatPage.chatThreadList.older.label',
  noMessage: 'chatPage.chatThreadList.error.noThreads',
  noResults: 'chatPage.chatThreadList.error.noResults',
} as const;

const ThreadsList = ({
  texts: threadSpecificText,
  threads,
  searchQuery,
  onSearch,
  onSortChange,
  headerAction,
  onLoadMore,
  isLoading,
  isSearchDisabled = false,
}: TThreadListProps) => {
  const intl = useIntl();

  const hasNoThreads = !threads || Object.values(threads).every((list) => list.length === 0);
  const hasSearchValue = searchQuery.trim().length > 0;
  const hasNoMatchingResults = hasNoThreads && hasSearchValue;
  const hasThreadsToDisplay = !hasNoThreads;

  const pagination = useAppSelector(selectPagination);

  return (
    <>
      <div className="display-flex justify-content-between margin-x-20">
        <p className="text-medium text-size-14">{threadSpecificText.title}</p>
        {hasThreadsToDisplay && headerAction}
      </div>

      {/* INFO: The inset and the spacing live here rather than in the field, so the field can also sit
      in layouts that own their own geometry, such as the question panel's toolbar row */}
      <div className="margin-top-5 margin-bottom-15 padding-x-20">
        {/* INFO: Thread search is disabled while a question is in flight because the answer reorders
        the list underneath it. The field itself knows nothing about chat, so the state is passed in. */}
        <SearchField testId={THREADS_LIST_TEST_IDS.search} isDisabled={isSearchDisabled} onSearch={onSearch} />
      </div>

      {/* INFO: No threads found for the search word */}
      {hasNoMatchingResults && (
        <div className="padding-x-20 text-center">
          <span data-testid="thread-search-no-results">{intl.formatMessage({ id: texts.noResults })}</span>
        </div>
      )}

      {/* INFO: No threads at all (only when NOT searching) */}
      {hasNoThreads && !hasSearchValue && (
        <div className="padding-x-20 text-center">
          <span data-testid="empty-threads-list">{threadSpecificText.noMessage}</span>
        </div>
      )}

      {/* INFO: Display sort controls and threads list when threads exist */}
      {hasThreadsToDisplay && (
        <>
          <ThreadsListControlBar changeSortOrder={onSortChange} />
          <Divider spacing={5} className="padding-0" />
          <div id={SCROLLABLE_SECTION}>
            <InfiniteScroll
              loadMore={onLoadMore}
              hasMore={pagination.hasNextPage}
              loader={
                <div className="padding-20" key={0}>
                  <Spinner />
                </div>
              }
              threshold={5}
              pageStart={0}
              initialLoad={false}
              isReverse={false}
              useWindow={false}
              getScrollParent={() => document.getElementById(SCROLLABLE_SECTION)}
            >
              {Object.entries(threads).map(([containedInTimespan, threads]) => {
                const threadsContainedInTimeSpan = threads as TChatThread[];
                if (hasValidThreads(threadsContainedInTimeSpan)) {
                  return (
                    <ThreadsInTimespanList
                      isLoading={isLoading}
                      key={containedInTimespan}
                      title={texts[containedInTimespan as keyof typeof texts]}
                      threads={threadsContainedInTimeSpan}
                      searchWord={searchQuery}
                    />
                  );
                }
                return null;
              })}
            </InfiniteScroll>
          </div>
        </>
      )}
    </>
  );
};

export default ThreadsList;
